import { DataError } from "../../core/errors/DataError";
import { enviroment } from "../../enviroment";
import { NAV_UNAVAILABLE_ERROR_CODE } from "../../data/models/mappers/mapError";

// Flag REACT_APP_NAV_UNAVAILABLE_ENABLED (NAV-ROBUSTHED-2026-09-29.md §7.2):
// the pure decisions of the "NAV (the item system) is down" flow, kept out
// of the toasts, the API client and the view-models so they can be
// unit-tested without react-query, react-toastify or the router.
//
// Prod fact (29/9 06:20-07:0x UTC): NAV could not reach its own SQL Server
// ("cannot connect the Change Listener … Timeout expired"); every NAV call
// waited 21 s for Windows' TCP-connect timeout. "Place order" and the
// delivery-date preview answered 400 with raw NAV text after 21 s, the
// item-list / lock / item lookups 500 after 21-30 s.
//
// With the API's own flag (NavFailFastEnabled) on, it answers those routes
// 503 { code: "navUnavailable", reason, retrySafe, retryAfterSeconds }
// straight from its NAV health latch (0 NAV calls) while NAV is down, and
// the same 503 (retrySafe false) when NAV is lost inside a request. On the
// FE:
//  - errorStatusFromBody keeps the marker as errorCode + retryAfterSeconds
//    + reason + retrySafe;
//  - withToasts shows ONE translated toast (nav_unavailable) instead of the
//    compound "Could not place order: <raw NAV text>" and rethrows, so every
//    caller's own finally still stops its spinner;
//  - place/commit order retry ONCE after retryAfterSeconds (clamped 2-15 s)
//    ONLY when retrySafe is true (navUnavailableRetry.ts);
//  - the confirmation page shows an amber "delivery date could not be
//    checked" line when the preview got this error (Place order stays
//    enabled);
//  - the three NAV lookups (delivery-date-preview, verify-storage,
//    navision/items) get a client-side AbortController timeout
//    (REACT_APP_NAV_LOOKUP_CLIENT_TIMEOUT_MS, unset = none) - the safety
//    net for the case where the API itself does not answer; an abort
//    surfaces as the same error family.
// Flag off = every helper here says "no" / passes the call through
// untouched: byte-identical DOM and wire.

export { NAV_UNAVAILABLE_ERROR_CODE };
// Marker of the client-side lookup timeout (no HTTP status - the request
// was cancelled before any answer). Carried as ErrorStatus.errorCode with
// code 0, reason "clientTimeout", retrySafe false.
export const NAV_LOOKUP_CLIENT_TIMEOUT_ERROR_CODE = "navLookupClientTimeout";
// i18n keys (all 10 locale files).
export const NAV_UNAVAILABLE_TOAST_KEY = "nav_unavailable";
export const NAV_UNAVAILABLE_RETRYING_KEY = "nav_unavailable_retrying";
export const NAV_UNAVAILABLE_DELIVERY_DATE_KEY = "nav_unavailable_delivery_date";
export const NAV_STALE_BADGE_KEY = "nav_stale_badge";
// The toast stays a little longer than the default 5 s: it is the only
// explanation the user gets, and it asks them to wait minutes.
export const NAV_UNAVAILABLE_TOAST_MS = 10_000;

function mentionsNavUnavailable(value: unknown): boolean {
  return (
    typeof value === "string" &&
    value.toLowerCase().includes(NAV_UNAVAILABLE_ERROR_CODE.toLowerCase())
  );
}

/**
 * The flag-FREE shape test: the API's 503 navUnavailable (marker kept as
 * errorCode by errorStatusFromBody, or named in the message / raw JSON body
 * when the envelope carried no message) or the client-side lookup timeout.
 */
export function isNavUnavailableFamily(error: unknown): boolean {
  if (!(error instanceof DataError)) return false;
  const status = error.errorStatus;
  if (!status) return false;
  if (status.errorCode === NAV_LOOKUP_CLIENT_TIMEOUT_ERROR_CODE) return true;
  if (status.code !== 503) return false;
  return (
    mentionsNavUnavailable(status.errorCode) ||
    mentionsNavUnavailable(status.message) ||
    mentionsNavUnavailable(error.message)
  );
}

/**
 * True for the API's 503 navUnavailable and for the client-side lookup
 * timeout - the errors that get the one toast / the amber line. Flag off =
 * always false, whatever the error.
 */
export function isNavUnavailableError(error: unknown): boolean {
  if (!enviroment.navUnavailableEnabled) return false;
  return isNavUnavailableFamily(error);
}

/**
 * True ONLY when the API said so explicitly (retrySafe: true = the latch
 * was open and the request was refused before any NAV call or write).
 * Anything else - the field missing, a non-boolean, retrySafe false, the
 * client timeout - is false: an order number may already have been drawn.
 */
export function isNavUnavailableRetrySafe(error: unknown): boolean {
  if (!isNavUnavailableError(error)) return false;
  return (error as DataError).errorStatus?.retrySafe === true;
}

/** The error an aborted NAV lookup surfaces as (same family). */
export function navLookupClientTimeoutError(timeoutMs: number): DataError {
  const seconds = Math.round(timeoutMs / 1000);
  const message = `The item lookup was cancelled after ${seconds} s without an answer from the API (client timeout)`;
  return new DataError(message, {
    code: 0,
    message,
    errorCode: NAV_LOOKUP_CLIENT_TIMEOUT_ERROR_CODE,
    reason: "clientTimeout",
    retrySafe: false,
  });
}

/**
 * The client timeout in ms for the NAV lookups, or undefined for none:
 * only when the flag is on AND REACT_APP_NAV_LOOKUP_CLIENT_TIMEOUT_MS parsed
 * to a positive value. Flag off = undefined even with a value set, so the
 * flag alone decides whether the wire changes.
 */
export function navLookupClientTimeoutMs(): number | undefined {
  if (!enviroment.navUnavailableEnabled) return undefined;
  const ms = enviroment.navLookupClientTimeoutMs;
  return typeof ms === "number" && Number.isFinite(ms) && ms > 0
    ? ms
    : undefined;
}

/**
 * Runs a NAV lookup under the client timeout. Without a timeout the request
 * is run exactly as before - `run` gets no signal, so the caller builds
 * today's RequestInit byte for byte (a caller-owned `outerSignal`, when
 * given, is what the caller uses itself). With one, an AbortController is
 * armed for the whole call (token acquisition + fetch + body read) and also
 * follows the caller's own signal, so the caller's abort still works; a
 * timer abort surfaces as navLookupClientTimeoutError, a caller abort and
 * every other rejection pass through untouched, and the timer is always
 * cleared.
 */
export async function withNavLookupClientTimeout<T>(
  run: (signal?: AbortSignal) => Promise<T>,
  outerSignal?: AbortSignal
): Promise<T> {
  const ms = navLookupClientTimeoutMs();
  if (ms === undefined) return run();
  const controller = new AbortController();
  let timedOut = false;
  const timer = setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, ms);
  const follow = () => controller.abort();
  if (outerSignal) {
    if (outerSignal.aborted) controller.abort();
    else outerSignal.addEventListener("abort", follow);
  }
  try {
    return await run(controller.signal);
  } catch (error) {
    if (timedOut && controller.signal.aborted) {
      throw navLookupClientTimeoutError(ms);
    }
    throw error;
  } finally {
    clearTimeout(timer);
    outerSignal?.removeEventListener("abort", follow);
  }
}
